import React, { useState, useEffect,useRef} from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { deleteDataFromApi, postDataFromApi } from "../../../services/api/apiService";
import LoaderWithText from "./LoaderWithText";
import { RestfulUrls } from "../../../constants/restfulUrls";

export interface OptionType {
  label: string;
  value: string;
}

interface CustomAutocompleteProps {
  options: OptionType[];
  addUrl: string;
  value: OptionType;
  onChange: (newValue: OptionType) => void;
  label: string;
  disabled?: boolean;
  autocompleteClass?: string;
  onOpen?: () => void;
  requiredMessage?: string;
  registerCancel?: (fn: () => void) => void;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  options,
  addUrl,
  value,
  onChange,
  label,
  disabled,
  autocompleteClass,
  onOpen,
  registerCancel
}) => {
  // ✅ Store user input
  const [inputValue, setInputValue] = useState<string>("");
  const [optionValue, setOptionValue] = useState<OptionType>(value);
  const [localOptions, setLocalOptions] = useState<OptionType[]>(() => {
    return options.length ? options : [];
  });
  const [showLoader, setShowLoader] = useState(false);
  const addedLabelIdRef = useRef<string | null>(null);
  const userHasChangedValueRef = useRef<boolean>(false);
  const prevValueRef = useRef<OptionType>(value);
  const dummyModalCloseTw = () => {};
  
  // ✅ Ensure previous text appears when modal opens and value prop changes
  // But don't reset if user is actively typing
  useEffect(() => {
    // Update local options when options prop changes
    setLocalOptions(options);
    
    // Detect when the parent has changed the selected value.
    // In that case we MUST sync internal state, even if the user previously typed/selected,
    // otherwise the component can keep showing an old label (e.g. when editing another row).
    const valueChanged = 
      value?.label !== prevValueRef.current?.label || 
      value?.value !== prevValueRef.current?.value;
    
    if (valueChanged) {
      // Parent-driven change: allow props to take over again
      userHasChangedValueRef.current = false;

      const newInputValue = value?.label || "";
      const newOptionValue = value || { label: "", value: "" };

      // Only update if values actually changed to avoid unnecessary re-renders
      if (
        newInputValue !== inputValue ||
        newOptionValue.label !== optionValue?.label ||
        newOptionValue.value !== optionValue?.value
      ) {
        setInputValue(newInputValue);
        setOptionValue(newOptionValue);
      }
    }
    
    // Update the ref to track the previous value
    prevValueRef.current = value || { label: "", value: "" };
  }, [value, options]);

  // ✅ Ensure `onBlur` saves new value in dropdown & local storage
  const handleBlur = () => {
    
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return; // Just return without showing validation message
    }

    const isExistingOption = localOptions.some(
      (option) => option.label.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (!isExistingOption) {
      setShowLoader(true);
      const newOption = { label: trimmedValue, value: trimmedValue };
      const payloadData = { label: newOption.label, category: "NON_EB" };
      try {
        postDataFromApi<any>(
          addUrl,
          payloadData,
          (response: any) => {
            const option: OptionType = {
              label: response.data.label,
              value: response.data.id,
            };
            setOptionValue(option);
            addedLabelIdRef.current = response.data.id;
            onChange(option);
            const updatedOptions = [...localOptions, option];
            setLocalOptions(updatedOptions);
            setShowLoader(false);
          },
          { isAuthRequired: true },
          undefined,
          (error: any) => {
            if (error) {
              setShowLoader(false);
            }
          }
        );
      } catch (error) {
        setShowLoader(false);
      }
    }
  };

  const revertAddedLabel = async () => {
    const labelId = addedLabelIdRef.current;
    if (labelId) {
      try {
        const url = `${RestfulUrls.BASE_URL}${RestfulUrls.DELETE_INTERVENTION_LABEL}/${labelId}`;
          await deleteDataFromApi<any>(
            url,
            null,
            () => {             
            }          
          );
      } catch (error) {
        console.error("Exception while deleting label:", error);
      } finally {
        addedLabelIdRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (registerCancel) {
      registerCancel(() => {
        revertAddedLabel();
      });
    }
  }, [registerCancel]);
  
  // Check if we're inside a popup by checking the autocompleteClass prop
  const isInsidePopup = autocompleteClass?.includes('popup') || false;
  
  // Use higher z-index if inside popup, otherwise use default
  // Popup has z-index 99999, so dropdown needs to be higher
  // Regular dropdowns keep their original z-index
  const wrapperZIndex = isInsidePopup ? 100000 : 10000;
  const dropdownZIndex = isInsidePopup ? 100001 : 10001;

  return (
    <Box 
      className={autocompleteClass || "country-textfield"} 
      sx={{ 
        position: "relative", 
        zIndex: wrapperZIndex,
        // Minimal styling for popup autocomplete
        ...(isInsidePopup && {
          '& .MuiAutocomplete-root': {
            '& .MuiOutlinedInput-root': {
              minHeight: '40px', // Smaller input height
            },
          },
        }),
      }}
    >
      <Autocomplete<OptionType, false, false, false>
        value={optionValue} // Keeps input independent from external updates
        onChange={(_event: React.SyntheticEvent, newValue: OptionType | null) => {
          const valueToSet: OptionType = newValue || { label: "", value: "" };
          userHasChangedValueRef.current = true; // Mark that user has made a change
          onChange(valueToSet);
          setOptionValue(valueToSet);
        }}
        // onChange={(_event, newValue: OptionType | null) => {
        //   if (newValue === null) {
        //     // User cleared the input
        //     revertAddedLabel();
        //     if (onCancel) onCancel();
        //     setOptionValue({ label: "", value: "" });
        //     onChange({ label: "", value: "" });
        //   } else {
        //     onChange(newValue);
        //     setOptionValue(newValue);
        //   }
        // }}
        disabled={disabled}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          userHasChangedValueRef.current = true; // Mark that user is typing
          setInputValue(newInputValue);
        }}
        options={localOptions}
        getOptionLabel={(option) => option.label}
        filterOptions={(options, state) =>
          options.filter((option) =>
            option.label.toLowerCase().includes(state.inputValue.toLowerCase())
          )
        }
        disablePortal={false} // Ensure dropdown renders in portal (outside popup) - critical for popup dropdowns
        slotProps={{
          popper: {
            sx: { 
              // Only apply special z-index and minimal design for popup dropdowns
              ...(isInsidePopup ? {
                zIndex: `${dropdownZIndex} !important` as any, // Force z-index with !important for production
                '& .MuiPaper-root': {
                  maxHeight: '200px !important', // Smaller max height for popup
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15) !important', // Minimal shadow
                  zIndex: `${dropdownZIndex} !important` as any, // Ensure paper is above popup
                },
                '& .MuiAutocomplete-listbox': {
                  padding: '4px 0 !important', // Minimal padding
                  maxHeight: '200px !important',
                },
                '& .MuiAutocomplete-option': {
                  minHeight: '32px !important', // Smaller option height
                  padding: '4px 12px !important', // Minimal padding
                  fontSize: '14px !important', // Smaller font
                },
              } : {
                // Regular dropdowns: use default MUI z-index, no styling changes
                zIndex: dropdownZIndex,
              }),
            },
            placement: "bottom-start",
            disablePortal: false, // Critical: render in portal to escape popup container
            ...(isInsidePopup && {
              style: {
                zIndex: dropdownZIndex, // Set z-index directly on popper element for popup only
              },
            }),
          },
          paper: {
            sx: { 
              // Only apply special styling for popup dropdowns
              ...(isInsidePopup ? {
                zIndex: `${dropdownZIndex} !important` as any, // Force z-index with !important
                maxHeight: '200px !important', // Smaller max height
                boxShadow: '0 2px 8px rgba(0,0,0,0.15) !important', // Minimal shadow
                position: 'relative',
                '& .MuiAutocomplete-listbox': {
                  padding: '4px 0 !important', // Minimal padding
                  maxHeight: '200px !important',
                  '& .MuiAutocomplete-option': {
                    minHeight: '32px !important', // Smaller option height
                    padding: '4px 12px !important', // Minimal padding
                    fontSize: '14px !important', // Smaller font
                  },
                },
              } : {
                // Regular dropdowns keep default styling - no changes
                zIndex: dropdownZIndex,
              }),
            },
            ...(isInsidePopup && {
              style: {
                zIndex: dropdownZIndex, // Set z-index directly on paper element for popup only
              },
            }),
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            onBlur={handleBlur} // ✅ Saves new entry on blur
            helperText="" // Remove validation message - using red border instead
            error={false} // Remove error state - using red border instead
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#d32f2f', // Red border
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#d32f2f', // Red border on hover
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#d32f2f', // Red border when focused
                  borderWidth: '1px',
                },
              },
            }}
          />
        )}
        onOpen={() => {
          if (onOpen) {
            onOpen(); // Call the parent-provided onOpen if it's passed
          }

          // Only clear the value if it's truly invalid (not in options and not empty)
          // Don't clear if the value is valid or if it's the initial value from props
          const isValid = localOptions.some(
            (option) => option.label === optionValue?.label
          );
          // Don't clear if the value is valid or if it's empty
          if (!isValid && optionValue?.label && optionValue.label.trim() !== "") {
            // Value is not in options, but keep it if it's a valid user selection
            // Only clear if it's truly invalid (e.g., a typo that doesn't match any option)
            // For now, we'll keep the value to allow user to select from dropdown
          }
        }}
        
      />

      {showLoader && (
        <LoaderWithText isOpen={showLoader} onClose={dummyModalCloseTw} />
      )}
    </Box>
  );
};

export default CustomAutocomplete;
